import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Icon, Input, Segmented, Select, Spinner, useLayout, useToast } from "@agentsaas/ui";
import { ApiError, type Asset, type MediaJob } from "@agentsaas/protocol";
import { errorMessage } from "@agentsaas/i18n";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { formatBytes, formatMicroUsd, formatRelative } from "../app/format";
import { useAgents } from "../data/queries";
import { ak, useAsset, useAssets, useMediaJobs, type AssetFilter } from "../data/assets";
import { CollabFrame } from "../project/ProjectChrome";
import { WorkSwitch } from "../project/WorkSwitch";
import { ASSET_KINDS, parseTags, uploadKindFor, type AssetKind } from "./model";
import { AssetThumb } from "./AssetThumb";

/**
 * /p/:pid/assets：资产库（筛选：类型 / 标签 / 搜索；网格缩略图；上传参考图）与生成任务（?tab=jobs）。
 * asset.ready 推送时资产列表与生成任务自动刷新（data/assets.ts）。
 */
export function AssetsPage() {
  const { pid = "" } = useParams();
  const mobile = useLayout() === "mobile";
  return (
    <CollabFrame pid={pid} section="assets">
      {mobile ? <WorkSwitch pid={pid} active="assets" /> : null}
      <AssetsView pid={pid} />
    </CollabFrame>
  );
}

type Tab = "library" | "jobs";
const TABS: Tab[] = ["library", "jobs"];

function AssetsView({ pid }: { pid: string }) {
  const { t } = useT();
  const [sp, setSp] = useSearchParams();
  const tab: Tab = sp.get("tab") === "jobs" ? "jobs" : "library";
  const setParam = (k: string, v: string | null) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );
  const [uploading, setUploading] = useState(false);
  const jobs = useMediaJobs(pid);
  const active = (jobs.data ?? []).filter((j) => j.state === "queued" || j.state === "running").length;

  return (
    <div className="page assets" data-testid="assets-page">
      <div className="page__head assets__head">
        <h1 className="page__title grow">{t("asset.title")}</h1>
        <Button icon="upload" onClick={() => setUploading(true)} data-testid="asset-upload">
          {t("asset.upload")}
        </Button>
      </div>
      <div className="sections__tabs assets__tabs" role="tablist" aria-label={t("asset.title")}>
        {TABS.map((x) => (
          <button
            key={x}
            type="button"
            role="tab"
            aria-selected={tab === x}
            className={`chip-tab${tab === x ? " is-active" : ""}`}
            onClick={() => setParam("tab", x === "jobs" ? "jobs" : null)}
            data-testid={`asset-tab-${x}`}
          >
            {t(`asset.tab.${x}`)}
            {x === "jobs" && active ? <span className="chip-tab__count">{active}</span> : null}
          </button>
        ))}
      </div>
      {tab === "library" ? <Library pid={pid} filter={{ kind: (sp.get("kind") as AssetKind) || undefined, tag: sp.get("tag") || undefined, q: sp.get("q") || undefined }} setParam={setParam} /> : <JobList pid={pid} />}
      <UploadDialog pid={pid} open={uploading} onClose={() => setUploading(false)} />
    </div>
  );
}

function Library({ pid, filter, setParam }: { pid: string; filter: AssetFilter; setParam(k: string, v: string | null): void }) {
  const { t } = useT();
  const assets = useAssets(pid, filter);
  const all = useAssets(pid, {});
  const tags = useMemo(() => [...new Set(all.items.flatMap((a) => a.tags))].sort(), [all.items]);
  const [q, setQ] = useState(filter.q ?? "");
  // 搜索框防抖写回 URL；与 URL 一致时不写（否则挂载时的空写入会用旧的 searchParams 覆盖掉刚选的筛选）
  useEffect(() => {
    if ((filter.q ?? "") === q.trim()) return;
    const h = setTimeout(() => setParam("q", q.trim() || null), 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  return (
    <>
      <div className="assets__filters">
        <Segmented<"all" | AssetKind>
          label={t("asset.filter.kind")}
          value={filter.kind ?? "all"}
          onChange={(v) => setParam("kind", v === "all" ? null : v)}
          options={[{ value: "all", label: t("asset.kind.all") }, ...ASSET_KINDS.filter((k) => k !== "audio" && k !== "other").map((k) => ({ value: k, label: t(`asset.kind.${k}`) }))]}
        />
        <Select aria-label={t("asset.filter.tag")} value={filter.tag ?? ""} onChange={(e) => setParam("tag", e.target.value || null)} data-testid="asset-tag-filter">
          <option value="">{t("asset.filter.all_tags")}</option>
          {tags.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
        <Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("asset.filter.search")} aria-label={t("asset.filter.search")} data-testid="asset-search" />
      </div>
      {assets.loading ? (
        <Spinner label={t("common.loading")} />
      ) : assets.items.length ? (
        <ul className="asset-grid" data-testid="asset-grid">
          {assets.items.map((a) => (
            <li key={a.id}>
              <AssetCard pid={pid} asset={a} />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="image" title={filter.kind || filter.tag || filter.q ? t("asset.empty_filtered") : t("asset.empty")}>
          <p className="small muted">{t("asset.empty_hint")}</p>
        </EmptyState>
      )}
      {assets.hasMore ? (
        <Button variant="ghost" block loading={assets.loadingMore} onClick={assets.loadMore}>
          {t("asset.load_more")}
        </Button>
      ) : null}
    </>
  );
}

export function Stars({ value, label }: { value: number | null; label: string }) {
  const full = value === null ? 0 : Math.round(value);
  return (
    <span className="stars" role="img" aria-label={label}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Icon key={i} name="star" size={14} className={i <= full ? "stars__on" : "stars__off"} />
      ))}
    </span>
  );
}

function AssetCard({ pid, asset }: { pid: string; asset: Asset }) {
  const { t } = useT();
  return (
    <Link to={`/p/${pid}/assets/${asset.id}`} className="asset-card" data-testid="asset-card" data-kind={asset.kind}>
      <AssetThumb asset={asset} version={asset.latestVersion} />
      <span className="asset-card__body">
        <strong className="ellipsis asset-card__name">{asset.name}</strong>
        <span className="asset-card__meta small muted">
          <Badge>{t(`asset.kind.${asset.kind}`)}</Badge>
          <span>{t("asset.version_short", { version: asset.latestVersion })}</span>
          <Stars value={asset.rating} label={asset.rating === null ? t("asset.unrated") : t("asset.rating_label", { rating: asset.rating })} />
        </span>
      </span>
    </Link>
  );
}

// ───────────── 生成任务 ─────────────

const JOB_TONE: Record<MediaJob["state"], "neutral" | "accent" | "success" | "danger"> = { queued: "neutral", running: "accent", succeeded: "success", failed: "danger" };

function JobList({ pid }: { pid: string }) {
  const { t, locale } = useT();
  const jobs = useMediaJobs(pid);
  const agents = useAgents(pid).data ?? [];
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const h = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(h);
  }, []);
  if (jobs.isPending) return <Spinner label={t("common.loading")} />;
  if (!jobs.data?.length) return <EmptyState icon="layers" title={t("asset.jobs.empty")} />;
  return (
    <ul className="list card job-list" data-testid="job-list">
      {jobs.data.map((j) => (
        <li key={j.jobId} className="list-item job" data-testid="job-item" data-state={j.state}>
          <Icon name={j.kind === "model3d" ? "cube" : "image"} />
          <span className="grow job__main">
            <span className="row-inline">
              <Badge tone={JOB_TONE[j.state]}>{t(`asset.jobs.state.${j.state}`)}</Badge>
              {j.kind === "model3d" ? (
                <span className="job__op" data-testid="job-op" data-op={j.op ?? "generate"}>
                  <Badge tone="accent">{t(`media.op.${j.op ?? "generate"}`)}</Badge>
                </span>
              ) : null}
              <span className="small">{t("asset.jobs.by", { agent: agents.find((a) => a.id === j.requestedBy)?.name ?? j.requestedBy, provider: j.provider })}</span>
            </span>
            {j.state === "running" || j.state === "queued" ? <JobProgress progress={j.progress ?? null} state={j.state} /> : null}
            {j.source ? <JobSource pid={pid} source={j.source} /> : null}
            <span className="small muted">
              {t("asset.jobs.cost", { cost: formatMicroUsd(j.estCostMicroUsd, locale) })}
              <span className="sep" />
              <time dateTime={j.createdAt}>{formatRelative(j.createdAt, locale, now)}</time>
            </span>
            {j.state === "failed" && j.error ? (
              <span className="small form-error">{errorMessage(locale, { code: j.error as Parameters<typeof errorMessage>[1]["code"] })}</span>
            ) : null}
          </span>
          {j.assetIds.length ? (
            <span className="job__assets">
              {j.assetIds.map((id) => (
                <Link key={id} to={`/p/${pid}/assets/${id}`} className="small" data-testid="job-asset">
                  {t("asset.jobs.open")}
                </Link>
              ))}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** 进度条：供应商报告了进度就显示百分比，否则（排队 / 未知）显示不定进度 */
export function JobProgress({ progress, state }: { progress: number | null; state: MediaJob["state"] }) {
  const { t } = useT();
  const known = progress !== null && state === "running";
  const pct = known ? Math.max(0, Math.min(100, Math.round(progress))) : null;
  return (
    <span className="job__progress small muted" data-testid="job-progress">
      <span
        className={`progress${pct === null ? " progress--indeterminate" : ""}`}
        role="progressbar"
        aria-label={t("media.job.progress_label")}
        aria-valuemin={0}
        aria-valuemax={100}
        {...(pct === null ? { "aria-valuetext": t(state === "queued" ? "asset.jobs.state.queued" : "media.job.progress_unknown") } : { "aria-valuenow": pct })}
      >
        <span className="progress__bar" style={pct === null ? undefined : { width: `${pct}%` }} />
      </span>
      <span>{pct === null ? t(state === "queued" ? "asset.jobs.state.queued" : "media.job.progress_unknown") : t("media.job.progress", { progress: pct })}</span>
    </span>
  );
}

/** 任务的来源资产版本（remesh / retexture / rig / animate / convert） */
function JobSource({ pid, source }: { pid: string; source: NonNullable<MediaJob["source"]> }) {
  const { t } = useT();
  const a = useAsset(source.assetId);
  return (
    <Link to={`/p/${pid}/assets/${source.assetId}?v=${source.version}`} className="small job__source" data-testid="job-source">
      {t("media.job.source", { name: a.data?.name ?? source.assetId, version: source.version })}
    </Link>
  );
}

// ───────────── 上传 ─────────────

function UploadDialog({ pid, open, onClose }: { pid: string; open: boolean; onClose(): void }) {
  const { t, locale } = useT();
  const { api, storageFetch } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const errText = useErrorText();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [tags, setTags] = useState("reference");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setName("");
    setTags("reference");
  }, [open]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const { kind, mediaType } = uploadKindFor(file);
      const up = await api.call("assetUpload", { params: { pid }, body: { kind, name: name.trim() || file.name, mediaType, size: file.size, tags: parseTags(tags) } });
      const res = await storageFetch(up.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": mediaType } });
      if (!res.ok) throw new ApiError("common.unavailable");
      void qc.invalidateQueries({ queryKey: ak.all(pid) });
      toast.show({ message: t("asset.uploaded", { name: up.asset.name }), tone: "success", duration: 2000 });
      onClose();
      navigate(`/p/${pid}/assets/${up.asset.id}`);
    } catch (err) {
      toast.show({ message: errText(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title={t("asset.upload_title")} closeLabel={t("shell.close")}>
      <form className="stack" onSubmit={(e) => void submit(e)} data-testid="upload-form">
        <p className="small muted">{t("asset.upload_hint")}</p>
        <Field label={t("asset.upload_file")}>
          {(id) => (
            <input
              id={id}
              type="file"
              accept="image/*,.glb,.gltf,.fbx,.obj,model/gltf-binary"
              className="ui-input"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                if (f && !name) setName(f.name.replace(/\.[^.]+$/, ""));
              }}
              data-testid="upload-file"
            />
          )}
        </Field>
        {file ? <p className="small muted">{t("asset.upload_size", { size: formatBytes(file.size, locale), kind: t(`asset.kind.${uploadKindFor(file).kind}`) })}</p> : null}
        <Field label={t("asset.upload_name")}>{(id) => <Input id={id} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} data-testid="upload-name" />}</Field>
        <Field label={t("asset.upload_tags")} hint={t("asset.upload_tags_hint")}>
          {(id, d) => <Input id={id} aria-describedby={d} value={tags} onChange={(e) => setTags(e.target.value)} data-testid="upload-tags" />}
        </Field>
        <Button type="submit" variant="primary" icon="upload" loading={busy} disabled={!file} data-testid="upload-submit">
          {t("asset.upload")}
        </Button>
      </form>
    </Dialog>
  );
}
